import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Input,
  PageShell,
  SectionCard,
  Skeleton,
  toast,
} from '@/components/ui';
import { useHasRole } from '@/providers/AuthProvider';
import { friendlyMessage } from '@/lib/errors';
import { PolicyToggleRow } from '@/features/orderPolicy/components/PolicyToggleRow';
import { PolicySelectRow } from '../components/PolicySelectRow';
import { useCartPolicyQuery, useCartPolicyMutation } from '../hooks/useCartPolicy';
import {
  useWishlistPolicyQuery,
  useWishlistPolicyMutation,
} from '../hooks/useWishlistPolicy';
import { SHOPPING_POLICY_ROLES } from '../lib/shoppingPolicyRbac';
import { clampInt, formatDateTime } from '../lib/format';
import {
  CART_MAX_QTY_HARD_LIMIT,
  DEFAULT_CART_POLICY,
  DEFAULT_WISHLIST_POLICY,
  type CartAddRedirect,
  type CartDupPolicy,
  type CartPolicyPayload,
  type CartPolicySettings,
  type CartSoldoutPolicy,
  type WishlistPolicyPayload,
  type WishlistPolicySettings,
} from '../types/shoppingPolicy';

/** 섹션 공통 — 로드 실패 시 재시도 카드. */
const ErrorCard = ({ message, onRetry }: { message: string; onRetry: () => void }) => {
  const { t } = useTranslation('common');
  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
      style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
    >
      <p>{message}</p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        {t('actions.retry', '다시 시도')}
      </Button>
    </div>
  );
};

/** 섹션 공통 — 스켈레톤 로더. */
const LoadingRows = ({ count }: { count: number }) => (
  <div role="status" aria-live="polite" className="space-y-3">
    {Array.from({ length: count }).map((_, i) => (
      <Skeleton key={i} className="h-[40px] w-full" />
    ))}
  </div>
);

/** 섹션 공통 — 최종 수정 메타 + 저장 버튼 바. */
const SaveBar = ({
  updatedBy,
  updatedAt,
  canWrite,
  saving,
  onSave,
  testId,
}: {
  updatedBy?: string | null | undefined;
  updatedAt?: string | null | undefined;
  canWrite: boolean;
  saving: boolean;
  onSave: () => void;
  testId: string;
}) => {
  const { t } = useTranslation('common');
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 rounded-xl p-4"
      style={{ background: 'var(--surface-raised)' }}
    >
      <span className="text-xs text-[color:var(--content-muted)]">
        {updatedBy || updatedAt
          ? `${t('shoppingPolicy.lastUpdated', '최종 수정')}: ${updatedBy ?? '-'} · ${formatDateTime(updatedAt)}`
          : t('shoppingPolicy.noHistory', '저장 이력이 없습니다.')}
      </span>
      <Button
        variant="cta"
        size="md"
        disabled={!canWrite || saving}
        onClick={onSave}
        data-testid={testId}
      >
        {saving ? t('actions.saving', '저장 중...') : t('actions.save', '저장')}
      </Button>
    </div>
  );
};

/**
 * 쇼핑 정책 설정 (고도몰 ID51_3). 한 화면에 2섹션 — 장바구니(order-service) /
 * 관심상품·간편구매(user-service). prefix 가 서로 다른 두 싱글톤 설정을 각각 GET 로드 →
 * 폼 시드, PUT 전체폼 저장(Idempotency-Key). RBAC=SUPER_ADMIN/OPS_ADMIN.
 *
 * 두 섹션은 독립 GET/PUT 이라 한쪽 로드 실패가 다른 쪽을 가리지 않는다(섹션별 로딩/에러 처리).
 */
export const ShoppingPolicySettingsPage = () => {
  const { t } = useTranslation('common');
  const canWrite = useHasRole(SHOPPING_POLICY_ROLES);

  const cartQuery = useCartPolicyQuery();
  const cartMutation = useCartPolicyMutation();
  const wishlistQuery = useWishlistPolicyQuery();
  const wishlistMutation = useWishlistPolicyMutation();

  // GET 결과를 폼 로컬 상태로 시드한다. 최초 도착 시에만 시드(편집 중 덮어쓰기 방지).
  const [cartForm, setCartForm] = useState<CartPolicySettings>(DEFAULT_CART_POLICY);
  const [cartSeeded, setCartSeeded] = useState(false);
  const [wishlistForm, setWishlistForm] = useState<WishlistPolicySettings>(
    DEFAULT_WISHLIST_POLICY,
  );
  const [wishlistSeeded, setWishlistSeeded] = useState(false);

  useEffect(() => {
    if (!cartSeeded && cartQuery.data) {
      setCartForm(cartQuery.data);
      setCartSeeded(true);
    }
  }, [cartSeeded, cartQuery.data]);

  useEffect(() => {
    if (!wishlistSeeded && wishlistQuery.data) {
      setWishlistForm(wishlistQuery.data);
      setWishlistSeeded(true);
    }
  }, [wishlistSeeded, wishlistQuery.data]);

  const updateCart = <K extends keyof CartPolicySettings>(
    key: K,
    value: CartPolicySettings[K],
  ): void => setCartForm((prev) => ({ ...prev, [key]: value }));

  const updateWishlist = <K extends keyof WishlistPolicySettings>(
    key: K,
    value: WishlistPolicySettings[K],
  ): void => setWishlistForm((prev) => ({ ...prev, [key]: value }));

  const handleSaveCart = () => {
    const payload: CartPolicyPayload = {
      cartAddRedirect: cartForm.cartAddRedirect,
      guestCartEnabled: cartForm.guestCartEnabled,
      cartRetentionDays: cartForm.cartRetentionDays,
      cartDupPolicy: cartForm.cartDupPolicy,
      cartSoldoutPolicy: cartForm.cartSoldoutPolicy,
      cartMaxQtyPerItem: cartForm.cartMaxQtyPerItem,
      cartMaxItems: cartForm.cartMaxItems,
    };
    cartMutation.mutate(payload, {
      onSuccess: (saved) => {
        setCartForm(saved);
        toast.success(t('shoppingPolicy.cart.saved', '장바구니 설정이 저장되었습니다.'));
      },
      onError: (err) =>
        toast.error(friendlyMessage(err, t('shoppingPolicy.saveError', '저장에 실패했습니다.'))),
    });
  };

  const handleSaveWishlist = () => {
    const payload: WishlistPolicyPayload = {
      wishlistEnabled: wishlistForm.wishlistEnabled,
      guestWishlistEnabled: wishlistForm.guestWishlistEnabled,
      wishlistMaxItems: wishlistForm.wishlistMaxItems,
      quickbuyEnabled: wishlistForm.quickbuyEnabled,
    };
    wishlistMutation.mutate(payload, {
      onSuccess: (saved) => {
        setWishlistForm(saved);
        toast.success(
          t('shoppingPolicy.wishlist.saved', '관심상품/간편구매 설정이 저장되었습니다.'),
        );
      },
      onError: (err) =>
        toast.error(friendlyMessage(err, t('shoppingPolicy.saveError', '저장에 실패했습니다.'))),
    });
  };

  const cartAddRedirectOptions: ReadonlyArray<{ value: CartAddRedirect; label: string }> = [
    { value: 'TO_CART', label: t('shoppingPolicy.cart.addRedirect.toCart', '장바구니로 이동') },
    { value: 'STAY', label: t('shoppingPolicy.cart.addRedirect.stay', '현재 위치 유지') },
    {
      value: 'CONTINUE_SHOPPING',
      label: t('shoppingPolicy.cart.addRedirect.continue', '쇼핑 계속'),
    },
  ];
  const cartDupOptions: ReadonlyArray<{ value: CartDupPolicy; label: string }> = [
    { value: 'MERGE', label: t('shoppingPolicy.cart.dup.merge', '기존 수량에 합산') },
    { value: 'SEPARATE', label: t('shoppingPolicy.cart.dup.separate', '별도 항목으로 추가') },
  ];
  const cartSoldoutOptions: ReadonlyArray<{ value: CartSoldoutPolicy; label: string }> = [
    { value: 'KEEP', label: t('shoppingPolicy.cart.soldout.keep', '유지(판매중지 표시)') },
    {
      value: 'AUTO_REMOVE',
      label: t('shoppingPolicy.cart.soldout.autoRemove', '자동 제외'),
    },
  ];

  return (
    <PageShell
      title={t('shoppingPolicy.title', '쇼핑 정책')}
      breadcrumb={[
        { label: t('sidebar.groups.settings', '기본 설정') },
        { label: t('shoppingPolicy.title', '쇼핑 정책') },
      ]}
      description={t(
        'shoppingPolicy.description',
        '장바구니 담기/보관/품절 처리와 관심상품·간편구매 사용 정책을 설정합니다.',
      )}
      data-testid="shopping-policy-page"
    >
      <div className="space-y-8">
        {/* ─────────── 섹션 1: 장바구니 (order-service) ─────────── */}
        <div className="space-y-6">
          <SectionCard
            title={t('shoppingPolicy.cart.sectionTitle', '장바구니 설정')}
            description={t(
              'shoppingPolicy.cart.sectionDesc',
              '상품 담기 후 동작·비회원 장바구니·보관기간·중복/품절 처리·최대 담기 수량을 설정합니다.',
            )}
          >
            {cartQuery.isPending ? (
              <LoadingRows count={6} />
            ) : cartQuery.isError ? (
              <ErrorCard
                message={t(
                  'shoppingPolicy.cart.loadError',
                  '장바구니 설정을 불러오지 못했습니다.',
                )}
                onRetry={() => void cartQuery.refetch()}
              />
            ) : (
              <>
                <PolicySelectRow<CartAddRedirect>
                  label={t('shoppingPolicy.cart.addRedirect.label', '담기 후 이동')}
                  description={t(
                    'shoppingPolicy.cart.addRedirect.desc',
                    '상품을 장바구니에 담은 뒤 이동할 위치를 선택합니다.',
                  )}
                  value={cartForm.cartAddRedirect}
                  options={cartAddRedirectOptions}
                  disabled={!canWrite}
                  onChange={(v) => updateCart('cartAddRedirect', v)}
                  testId="cart-policy-add-redirect"
                />
                <div className="border-t" style={{ borderColor: 'var(--surface-1)' }}>
                  <PolicySelectRow<CartDupPolicy>
                    label={t('shoppingPolicy.cart.dup.label', '동일 상품 재담기')}
                    description={t(
                      'shoppingPolicy.cart.dup.desc',
                      '이미 담긴 상품을 다시 담을 때 처리 방식을 선택합니다.',
                    )}
                    value={cartForm.cartDupPolicy}
                    options={cartDupOptions}
                    disabled={!canWrite}
                    onChange={(v) => updateCart('cartDupPolicy', v)}
                    testId="cart-policy-dup"
                  />
                </div>
                <div className="border-t" style={{ borderColor: 'var(--surface-1)' }}>
                  <PolicySelectRow<CartSoldoutPolicy>
                    label={t('shoppingPolicy.cart.soldout.label', '품절 상품 처리')}
                    description={t(
                      'shoppingPolicy.cart.soldout.desc',
                      '장바구니에 담긴 상품이 품절되면 처리할 방식을 선택합니다.',
                    )}
                    value={cartForm.cartSoldoutPolicy}
                    options={cartSoldoutOptions}
                    disabled={!canWrite}
                    onChange={(v) => updateCart('cartSoldoutPolicy', v)}
                    testId="cart-policy-soldout"
                  />
                </div>
                <div className="border-t" style={{ borderColor: 'var(--surface-1)' }}>
                  <PolicyToggleRow
                    label={t('shoppingPolicy.cart.guest.label', '비회원 장바구니 사용 · 표시 전용')}
                    description={t(
                      'shoppingPolicy.cart.guest.desc',
                      '현재 비회원 장바구니는 별도 제어 없이 동작하며, 이 설정은 표시 전용입니다.',
                    )}
                    checked={cartForm.guestCartEnabled}
                    disabled
                    onChange={(v) => updateCart('guestCartEnabled', v)}
                    testId="cart-policy-guest"
                  />
                </div>
                <div
                  className="flex flex-col border-t pt-3"
                  style={{ borderColor: 'var(--surface-1)' }}
                >
                  <label
                    htmlFor="cart-retention-days"
                    className="text-sm font-medium text-[color:var(--content-strong)]"
                  >
                    {t('shoppingPolicy.cart.retention.label', '장바구니 보관기간 (일)')}
                  </label>
                  <span className="mb-1.5 text-xs text-[color:var(--content-muted)]">
                    {t(
                      'shoppingPolicy.cart.retention.desc',
                      '담은 뒤 이 기간이 지난 장바구니는 자동으로 정리됩니다. (최소 1일)',
                    )}
                  </span>
                  <Input
                    id="cart-retention-days"
                    type="number"
                    min={1}
                    inputMode="numeric"
                    disabled={!canWrite}
                    className="w-40"
                    value={cartForm.cartRetentionDays}
                    onChange={(e) =>
                      updateCart('cartRetentionDays', clampInt(e.target.value, 1))
                    }
                    data-testid="cart-policy-retention-days"
                  />
                </div>
                <div
                  className="flex flex-col border-t pt-3"
                  style={{ borderColor: 'var(--surface-1)' }}
                >
                  <label
                    htmlFor="cart-max-qty"
                    className="text-sm font-medium text-[color:var(--content-strong)]"
                  >
                    {t('shoppingPolicy.cart.maxQty.label', '상품당 최대 담기 수량')}
                  </label>
                  <span className="mb-1.5 text-xs text-[color:var(--content-muted)]">
                    {t(
                      'shoppingPolicy.cart.maxQty.desc',
                      '한 상품을 장바구니에 담을 수 있는 최대 수량입니다. (1~50,000)',
                    )}
                  </span>
                  <Input
                    id="cart-max-qty"
                    type="number"
                    min={1}
                    max={CART_MAX_QTY_HARD_LIMIT}
                    inputMode="numeric"
                    disabled={!canWrite}
                    className="w-40"
                    value={cartForm.cartMaxQtyPerItem}
                    onChange={(e) =>
                      updateCart(
                        'cartMaxQtyPerItem',
                        clampInt(e.target.value, 1, CART_MAX_QTY_HARD_LIMIT),
                      )
                    }
                    data-testid="cart-policy-max-qty"
                  />
                </div>
                <div
                  className="flex flex-col border-t pt-3"
                  style={{ borderColor: 'var(--surface-1)' }}
                >
                  <label
                    htmlFor="cart-max-items"
                    className="text-sm font-medium text-[color:var(--content-strong)]"
                  >
                    {t('shoppingPolicy.cart.maxItems.label', '최대 담을 수 있는 상품 수')}
                  </label>
                  <span className="mb-1.5 text-xs text-[color:var(--content-muted)]">
                    {t(
                      'shoppingPolicy.cart.maxItems.desc',
                      '장바구니에 담을 수 있는 서로 다른 상품(라인)의 최대 개수입니다. 0 = 무제한. (0 이상)',
                    )}
                  </span>
                  <Input
                    id="cart-max-items"
                    type="number"
                    min={0}
                    inputMode="numeric"
                    disabled={!canWrite}
                    className="w-40"
                    value={cartForm.cartMaxItems}
                    onChange={(e) =>
                      // BE 클램프 = Math.max(0, x) — 하한 0만, 상한 캡 없음(clampInt max 미지정).
                      updateCart('cartMaxItems', clampInt(e.target.value, 0))
                    }
                    data-testid="cart-policy-max-items"
                  />
                </div>
              </>
            )}
          </SectionCard>

          {!cartQuery.isPending && !cartQuery.isError && (
            <SaveBar
              updatedBy={cartForm.updatedBy}
              updatedAt={cartForm.updatedAt}
              canWrite={canWrite}
              saving={cartMutation.isPending}
              onSave={handleSaveCart}
              testId="cart-policy-save"
            />
          )}
        </div>

        {/* ─────────── 섹션 2: 관심상품/간편구매 (user-service) ─────────── */}
        <div className="space-y-6">
          <SectionCard
            title={t('shoppingPolicy.wishlist.sectionTitle', '관심상품 · 간편구매 설정')}
            description={t(
              'shoppingPolicy.wishlist.sectionDesc',
              '관심상품(찜) 기능 사용 여부와 최대 보유 수, 간편구매(바로구매) 사용 여부를 설정합니다.',
            )}
          >
            {wishlistQuery.isPending ? (
              <LoadingRows count={4} />
            ) : wishlistQuery.isError ? (
              <ErrorCard
                message={t(
                  'shoppingPolicy.wishlist.loadError',
                  '관심상품/간편구매 설정을 불러오지 못했습니다.',
                )}
                onRetry={() => void wishlistQuery.refetch()}
              />
            ) : (
              <>
                <PolicyToggleRow
                  label={t('shoppingPolicy.wishlist.enabled.label', '관심상품 사용')}
                  description={t(
                    'shoppingPolicy.wishlist.enabled.desc',
                    '상품을 관심상품(찜)으로 담는 기능을 사용합니다. 끄면 신규 등록이 차단됩니다.',
                  )}
                  checked={wishlistForm.wishlistEnabled}
                  disabled={!canWrite}
                  onChange={(v) => updateWishlist('wishlistEnabled', v)}
                  testId="wishlist-policy-enabled"
                />
                <div className="border-t" style={{ borderColor: 'var(--surface-1)' }}>
                  <PolicyToggleRow
                    label={t('shoppingPolicy.wishlist.guest.label', '비회원 관심상품 사용 · 표시 전용')}
                    description={t(
                      'shoppingPolicy.wishlist.guest.desc',
                      '현재 비회원 관심상품은 회원 전용으로 동작하며, 이 설정은 표시 전용입니다.',
                    )}
                    checked={wishlistForm.guestWishlistEnabled}
                    disabled
                    onChange={(v) => updateWishlist('guestWishlistEnabled', v)}
                    testId="wishlist-policy-guest"
                  />
                </div>
                <div
                  className="flex flex-col border-t pt-3"
                  style={{ borderColor: 'var(--surface-1)' }}
                >
                  <label
                    htmlFor="wishlist-max-items"
                    className="text-sm font-medium text-[color:var(--content-strong)]"
                  >
                    {t('shoppingPolicy.wishlist.maxItems.label', '최대 관심상품 수')}
                  </label>
                  <span className="mb-1.5 text-xs text-[color:var(--content-muted)]">
                    {t(
                      'shoppingPolicy.wishlist.maxItems.desc',
                      '회원 1인당 담을 수 있는 관심상품의 최대 개수입니다. (최소 1)',
                    )}
                  </span>
                  <Input
                    id="wishlist-max-items"
                    type="number"
                    min={1}
                    inputMode="numeric"
                    disabled={!canWrite || !wishlistForm.wishlistEnabled}
                    className="w-40"
                    value={wishlistForm.wishlistMaxItems}
                    onChange={(e) =>
                      updateWishlist('wishlistMaxItems', clampInt(e.target.value, 1))
                    }
                    data-testid="wishlist-policy-max-items"
                  />
                </div>
                <div className="border-t" style={{ borderColor: 'var(--surface-1)' }}>
                  <PolicyToggleRow
                    label={t('shoppingPolicy.wishlist.quickbuy.label', '간편구매 사용')}
                    description={t(
                      'shoppingPolicy.wishlist.quickbuy.desc',
                      '장바구니를 거치지 않고 바로 구매하는 간편구매(바로구매) 버튼을 노출합니다.',
                    )}
                    checked={wishlistForm.quickbuyEnabled}
                    disabled={!canWrite}
                    onChange={(v) => updateWishlist('quickbuyEnabled', v)}
                    testId="wishlist-policy-quickbuy"
                  />
                </div>
              </>
            )}
          </SectionCard>

          {!wishlistQuery.isPending && !wishlistQuery.isError && (
            <SaveBar
              updatedBy={wishlistForm.updatedBy}
              updatedAt={wishlistForm.updatedAt}
              canWrite={canWrite}
              saving={wishlistMutation.isPending}
              onSave={handleSaveWishlist}
              testId="wishlist-policy-save"
            />
          )}
        </div>
      </div>
    </PageShell>
  );
};
